import { z } from 'zod';
import { IsoDateSchema, MoneyStringSchema } from './primitives.js';
/**
 * Voucher TENDERS (bugs: RV-02…RV-06, PV-01 — "one receipt can use several
 * methods at once": 5,000 ILS cash + 1,000 USD cash + 2,000 by card + 50,000
 * spread over ten cheques, ONE document, each cheque with its own due date,
 * totals per currency and a grand total in the base currency).
 *
 * A receipt or payment voucher now carries one row per way the money moved.
 * The voucher's old single `method / amount / currency / receivedIntoAccountId`
 * columns stay as a DERIVED SUMMARY — `method` reads `mixed` when more than
 * one tender is on the document, `amount` is the total in the voucher's own
 * currency — so every list and report that reads the head keeps working.
 *
 * RATE CONVENTION — GEN-109a, the one every frozen rate in the product uses:
 * units of the TENDER's currency per ONE base unit; base→foreign multiplies,
 * foreign→base divides. `baseAmount = amount ÷ rate`. The base currency's own
 * rate is 1 and the server fixes it so.
 *
 * Shared by `receipts.ts` and `payments.ts`; the payment side adds the one
 * method a receipt cannot have — `endorsed_check`, a cheque received from a
 * customer and handed on to a supplier (PV-01).
 */
export const RECEIPT_TENDER_METHODS = ['cash', 'card', 'bank_transfer', 'check', 'online'];
export const PAYMENT_TENDER_METHODS = [...RECEIPT_TENDER_METHODS, 'endorsed_check'];
export const ReceiptTenderMethodSchema = z.enum(RECEIPT_TENDER_METHODS);
export const PaymentTenderMethodSchema = z.enum(PAYMENT_TENDER_METHODS);
/**
 * The rate a tender converts by. Wider than FIN-05's four-decimal table rate
 * because a typed cross rate (ILS per USD, say) is often quoted to more; the
 * column is numeric(18,8).
 */
export const TenderRateSchema = z
    .string()
    .regex(/^\d{1,9}(\.\d{1,8})?$/, 'Rates are decimal strings with at most 8 decimals')
    .refine((value) => !/^0+(\.0+)?$/.test(value), 'A rate must be above zero.');
const chequeText = (max) => z.string().trim().max(max).optional();
const tenderInputBase = {
    /** ISO-4217, upper case. Defaults to the voucher's own currency when absent. */
    currency: z
        .string()
        .trim()
        .regex(/^[A-Za-z]{3}$/)
        .transform((code) => code.toUpperCase())
        .optional(),
    /** Required — except on an endorsed cheque, which copies the register's amount. */
    amount: MoneyStringSchema.optional(),
    /** Absent = the rate table's newest row on or before the voucher date (GEN-110); 1 for the base. */
    rate: TenderRateSchema.optional(),
    /** The cash / bank / cheques-in-hand account this tender moves through. Required unless the tender is an endorsed cheque. */
    accountId: z.uuid().optional(),
    reference: chequeText(60),
    // ── Cheque fields (RV-04's column order: no → bank → branch → account no → amount → currency → due date → rate → notes) ──
    chequeNo: chequeText(40),
    bankName: chequeText(120),
    branchNo: chequeText(40),
    accountNo: chequeText(60),
    dueDate: IsoDateSchema.optional(),
    /** PV-01: the in-hand cheque an `endorsed_check` tender hands on. */
    chequeId: z.uuid().optional(),
    notes: chequeText(500),
};
function tenderRules(method, row, ctx) {
    if (method !== 'endorsed_check' && row.amount === undefined) {
        ctx.addIssue({ code: 'custom', path: ['amount'], message: TENDER_MESSAGES.amountRequired });
    }
    if (method === 'check') {
        if (!row.chequeNo) {
            ctx.addIssue({ code: 'custom', path: ['chequeNo'], message: TENDER_MESSAGES.chequeNoRequired });
        }
        if (!row.dueDate) {
            ctx.addIssue({ code: 'custom', path: ['dueDate'], message: TENDER_MESSAGES.dueDateRequired });
        }
    }
    if (method === 'endorsed_check') {
        if (!row.chequeId) {
            ctx.addIssue({ code: 'custom', path: ['chequeId'], message: TENDER_MESSAGES.endorsedChequeRequired });
        }
    }
    else if (!row.accountId) {
        ctx.addIssue({ code: 'custom', path: ['accountId'], message: TENDER_MESSAGES.accountRequired });
    }
}
export const ReceiptTenderInputSchema = z
    .object({ method: ReceiptTenderMethodSchema, ...tenderInputBase })
    .strict()
    .superRefine((row, ctx) => tenderRules(row.method, row, ctx));
export const PaymentTenderInputSchema = z
    .object({ method: PaymentTenderMethodSchema, ...tenderInputBase })
    .strict()
    .superRefine((row, ctx) => tenderRules(row.method, row, ctx));
/** At least one way the money moved — a voucher with no tender has no amount. */
export const ReceiptTendersInputSchema = ReceiptTenderInputSchema.array().min(1, 'At least one tender is required.');
export const PaymentTendersInputSchema = PaymentTenderInputSchema.array().min(1, 'At least one tender is required.');
const tenderResponseBase = {
    id: z.uuid(),
    lineNo: z.number().int().positive(),
    currency: z.string(),
    amount: z.string(),
    rate: z.string(),
    /** `amount ÷ rate`, 2 dp, in the company's base currency. */
    baseAmount: z.string(),
    accountId: z.uuid().nullable(),
    accountCode: z.string().nullable(),
    reference: z.string().nullable(),
    chequeNo: z.string().nullable(),
    bankName: z.string().nullable(),
    branchNo: z.string().nullable(),
    accountNo: z.string().nullable(),
    dueDate: IsoDateSchema.nullable(),
    /** The register row this tender created (a cheque received or written) or endorsed. */
    chequeId: z.uuid().nullable(),
    /** ICQ-/OCQ- code of that register row, for the screen's link. */
    chequeCode: z.string().nullable(),
    notes: z.string().nullable(),
    /**
     * FIN-10 §6 PER TENDER: when THIS cheque bounced and was reversed, and the
     * entry that undid its own leg (DR Trade Receivables · CR the account the
     * tender debited, for the tender's amount). The other tenders on the voucher
     * keep counting; the head's `amount` and allocations shrink by this one's
     * share, and the voucher is `reversedAt`-stamped only once EVERY tender has
     * bounced. Receipt side only — a payment cheque's bounce still reverses the
     * whole voucher, so a payment tender carries null here.
     */
    bouncedAt: z.string().nullable(),
    bounceEntryId: z.uuid().nullable(),
};
export const ReceiptTenderSchema = z
    .object({ method: ReceiptTenderMethodSchema, ...tenderResponseBase })
    .strict();
export const PaymentTenderSchema = z
    .object({ method: PaymentTenderMethodSchema, ...tenderResponseBase })
    .strict();
/** RV-06 — a subtotal per currency used on the document. */
export const TenderCurrencyTotalSchema = z
    .object({ currency: z.string(), amount: z.string() })
    .strict();
/** The fields a voucher response gains, spread into both heads. */
export const tenderSummaryFields = {
    tenderTotals: TenderCurrencyTotalSchema.array(),
    /** Σ baseAmount — the grand total in the company's base currency. */
    tendersBaseTotal: z.string(),
};
export const TENDER_MESSAGES = {
    atLeastOne: 'At least one tender is required.',
    notBoth: 'Send either tenders or the single method and amount, not both.',
    amountRequired: 'Each tender needs its amount.',
    chequeNoRequired: 'A cheque tender needs its cheque number.',
    dueDateRequired: 'A cheque tender needs its due date.',
    accountRequired: 'Each tender names the account the money moved through.',
    endorsedChequeRequired: 'An endorsed-cheque tender names the cheque being handed on.',
    endorsedChequeNotInHand: 'Only a cheque still in hand can be endorsed to a supplier.',
    endorsedChequeOnReceipt: 'A receipt cannot carry an endorsed cheque.',
};
//# sourceMappingURL=tenders.js.map